/**
 * Accordion + swapping media.
 *
 * Left column: a list of accordion items (rendered via the shared `accordion-item`
 * snippet, toggled by the global AccordionController). Each item with an image
 * renders it as a layer inside its accordion panel, which is where it shows on mobile.
 *
 * From 768px the layers are moved into the right column as a stack of
 * absolutely-positioned images, and moved back into their panels below that, so
 * rotating a tablet or resizing a window swaps between the two layouts.
 *
 * The AccordionController dispatches `accordion:change` on `window` whenever an
 * item opens/closes. When an item inside this section opens, we reveal the layer
 * from its panel. Closing an item, or opening one without an image, does nothing
 * (the last-shown image stays visible).
 *
 * Hook: [data-accordion-media] wrapping [data-accordion-media-items] (buttons and
 * panels holding [data-accordion-media-layer]) and [data-accordion-media-figure].
 */
const SECTION = "[data-accordion-media]";
const DESKTOP = window.matchMedia("(min-width: 768px)");

class AccordionMedia {
  constructor(section) {
    this.section = section;
    this.items = section.querySelector("[data-accordion-media-items]");
    this.figure = section.querySelector("[data-accordion-media-figure]");
    if (!this.items || !this.figure) return;

    // Remember each layer's panel so it can be returned there on mobile.
    this.layers = Array.from(this.items.querySelectorAll("[data-accordion-media-layer]")).map(
      (layer) => ({ layer, panel: layer.parentElement }),
    );
    if (this.layers.length === 0) {
      this.figure.hidden = true;
      return;
    }

    const open = this.layers.find(({ panel }) => this.isOpen(panel));
    if (open) this.show(open.layer);

    this.place = this.place.bind(this);
    this.onChange = this.onChange.bind(this);
    DESKTOP.addEventListener("change", this.place);
    window.addEventListener("accordion:change", this.onChange);
    this.place();
  }

  isOpen(panel) {
    return Boolean(panel.previousElementSibling?.classList.contains("active"));
  }

  place() {
    if (DESKTOP.matches) {
      this.layers.forEach(({ layer }) => this.figure.appendChild(layer));
      return;
    }

    this.layers.forEach(({ layer, panel }) => {
      panel.appendChild(layer);
      // An open panel's max-height was measured without the image — re-measure it.
      if (this.isOpen(panel) && panel.style.maxHeight.endsWith("px")) {
        panel.style.maxHeight = panel.scrollHeight + "px";
      }
    });
  }

  onChange(event) {
    const { accordion, expanded } = event.detail || {};
    if (!expanded || !accordion || !this.items.contains(accordion)) return;

    const match = this.layers.find(({ panel }) => panel === accordion.nextElementSibling);
    if (match) this.show(match.layer);
  }

  show(active) {
    this.layers.forEach(({ layer }) => {
      layer.classList.toggle("is-active", layer === active);
    });
  }
}

export function initAccordionMedia(root = document) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll(SECTION).forEach((section) => {
    if (section.dataset.accordionMediaInit === "true") return;
    section.dataset.accordionMediaInit = "true";
    new AccordionMedia(section);
  });
}

export default initAccordionMedia;
